import { formatDistanceToNowStrict } from 'date-fns';

/**
 * The locale every formatter here renders in.
 *
 * ⚠️ **A module-level value set once, rather than an argument on 68 files' worth of call
 * sites.** `absolute` and `relative` are called from everywhere and take a single ISO
 * string; threading a locale through each would be a mechanical change to hundreds of
 * lines, and the first call site that forgot would render one date on a page in a
 * different convention from the one beside it — a difference nobody would report as a bug
 * because each looks plausible alone.
 *
 * ⚠️ **`LocaleProvider` owns the write and re-renders the tree**, so this is not a hidden
 * global somebody has to remember to refresh: the provider sets it from the resolved
 * preference and changes a context value in the same pass. Reading it here keeps the
 * formatters pure functions of (value, current locale) rather than hooks, which is what
 * lets them stay callable from `exportValue`, a sort comparator or a test.
 *
 * ⚠️ **`undefined` would mean the BROWSER's locale**, which is what this replaces: two
 * people looking at one incident would read different dates, and a screenshot in a support
 * ticket would not match what the reporter saw.
 */
let currentLocale: string = 'en-GB';

/** Set by `LocaleProvider` when the tenant's resolved preference arrives. */
export function setDisplayLocale(locale: string): void {
  currentLocale = locale;
}

/** What the formatters are using right now. Exported for tests and for the export helpers. */
export function displayLocale(): string {
  return currentLocale;
}

/**
 * A timestamp as a distance from now — with its DIRECTION.
 *
 * The suffix used to be a hard-coded " ago", which was right for every caller
 * there was: created, last seen, occurred, all in the past. The first future
 * date to reach it was an external report recipient's expiry, and the panel
 * cheerfully described an address that stops receiving in six months as one
 * that stopped six months ago — the exact opposite of the fact that column
 * exists to convey.
 *
 * `addSuffix` produces "6 months ago" for a past instant, character for
 * character what the old form produced, and "in 6 months" for a future one. So
 * this is a fix for every caller rather than a change to any of them.
 */
export function relative(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return formatDistanceToNowStrict(new Date(iso), { addSuffix: true });
  } catch {
    return '—';
  }
}

export function absolute(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(currentLocale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * The weekday of a UTC calendar day — `2026-09-11` → `Friday`.
 *
 * ⚠️ **Read in UTC, never in the reader's zone.** Every daily bucket on this
 * platform is UTC by decision, and a bare `2026-09-11` rendered locally names
 * the day BEFORE anywhere west of Greenwich — so a Saturday finding would read
 * "Friday" in New York, which is exactly the fact the weekday was shown to
 * answer. Nothing about it would look wrong.
 *
 * Returns the day UNJUDGED: it names Saturday and does not call it a non-working
 * day. Which days a fleet works is tenant configuration (`operating.*`, parent
 * owned), so a weekend rule written here would be a second copy of that calendar
 * and wrong for every fleet that runs on a Saturday.
 */
export function weekday(day: string | null | undefined): string {
  if (!day) return '';
  const at = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleDateString(currentLocale, { weekday: 'long', timeZone: 'UTC' });
}

/** Shorten a uuid for dense display while keeping it copy-recognisable. */
export function shortId(id: string | null | undefined): string {
  if (!id) return '—';
  return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

export function titleCase(value: string): string {
  return value
    .toLowerCase()
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function number(value: number | null | undefined): string {
  return value == null ? '—' : value.toLocaleString();
}

/**
 * A span of seconds as words: `240` → `4 minutes`.
 *
 * Written because the recommendation page showed `sustainedSeconds` as a bare
 * `240` — the single most important number on a screen about unbelted motorway
 * driving, in the unit the bus happened to report it in. Four minutes is a
 * length of time a person can picture; 240 is a reading.
 *
 * **Never rounded to a nicer number.** A remainder is carried rather than
 * dropped, because this feeds a screen where somebody decides whether an
 * incident is real, and "about 4 minutes" invites an argument that the exact
 * figure settles. Sub-minute spans keep one decimal only when they have one.
 */
export function duration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  if (seconds < 0) return '—';

  const unit = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

  if (seconds < 60) {
    // 0.5 stays 0.5; 45 stays 45. `Number()` drops a trailing `.0`.
    return unit(Number(seconds.toFixed(1)), 'second');
  }

  const whole = Math.round(seconds);
  if (whole < 3600) {
    const m = Math.floor(whole / 60);
    const s = whole % 60;
    return s === 0 ? unit(m, 'minute') : `${unit(m, 'minute')} ${unit(s, 'second')}`;
  }

  const h = Math.floor(whole / 3600);
  const m = Math.round((whole % 3600) / 60);
  // 90 minutes of remainder cannot exist, but 60 can after rounding — and
  // "1 hour 60 minutes" is the kind of thing nobody notices until a customer does.
  if (m === 60) return unit(h + 1, 'hour');
  return m === 0 ? unit(h, 'hour') : `${unit(h, 'hour')} ${unit(m, 'minute')}`;
}

/**
 * Minutes as a COMPACT span — 540 → "9 h", 570 → "9 h 30 m".
 *
 * ⚠️ **Beside `duration` rather than folded into it, and the two are not duplicates.** That one
 * takes SECONDS and writes prose — "9 hours 30 minutes" — for an incident page where a reader
 * is deciding whether something is real and an exact figure settles an argument. This one is
 * for a dense table cell and a select label, where the long form wraps and pushes a column out
 * of line. Minutes because that is how every drivers'-hours limit is carried on the wire.
 *
 * Shared because three surfaces now read it — the ruleset table, the per-vehicle picker and the
 * editor's live helper text — and a second copy of "how a span is abbreviated" is how one
 * screen says `9 h 30 m` and the next says `9.5h`. The `initials` lesson, one file along.
 */
export function hours(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} m`;
}

/**
 * Someone's initials, for an avatar.
 *
 * Splits on spaces, `@` and `.` so an account with no display name still
 * produces two letters from its address rather than one from "sumant.patro".
 *
 * Shared because it is now used in two places — the account menu and the
 * copilot transcript — and a second copy of "how a person is abbreviated" is
 * how one surface starts calling somebody SP and the other S.
 */
export function initials(name: string): string {
  return name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
}
